import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, Loader2, Users, Mail, Trash2, Search, 
  UserPlus, RefreshCw, ExternalLink 
} from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import EmailMergeDialog from '../../components/EmailMergeDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
  { value: 'negotiating', label: 'Negotiating', color: 'bg-orange-100 text-orange-700' },
  { value: 'won', label: 'Won', color: 'bg-green-100 text-green-700' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700' },
];

const getStatusBadge = (status) => {
  const s = STATUSES.find(x => x.value === status) || STATUSES[0];
  return <Badge className={s.color}>{s.label}</Badge>;
};

export default function FranchiseListDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [list, setList] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [templates, setTemplates] = useState([]);
  
  // Email Merge Dialog
  const [emailMergeOpen, setEmailMergeOpen] = useState(false);
  
  // Remove lead dialog
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [leadToRemove, setLeadToRemove] = useState(null);

  // Check for action=email in URL
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'email') {
      setEmailMergeOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    loadList();
    loadTemplates();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/franchise/lists/${id}`);
      setList(res.data);
      setLeads(res.data.leads || []);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('List not found');
        navigate('/admin/franchise/lists');
      } else if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/app');
      } else {
        toast.error('Failed to load list');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/templates?tag=franchise`);
      setTemplates(res.data || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleRemoveLead = async () => {
    if (!leadToRemove) return;
    try {
      await axios.delete(`${API}/franchise/leads/${leadToRemove.id}/lists/${id}`);
      toast.success('Lead removed from list');
      setLeads(leads.filter(l => l.id !== leadToRemove.id));
      setRemoveDialogOpen(false);
      setLeadToRemove(null);
    } catch (error) {
      toast.error('Failed to remove lead');
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (lead.first_name || '').toLowerCase().includes(q) ||
      (lead.last_name || '').toLowerCase().includes(q) ||
      (lead.email || '').toLowerCase().includes(q) ||
      (lead.work_email || '').toLowerCase().includes(q) ||
      (lead.current_employer || '').toLowerCase().includes(q) ||
      (lead.city || '').toLowerCase().includes(q)
    );
  });

  const leadsWithEmail = leads.filter(l => l.email || l.work_email);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!list) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/franchise/lists')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: list.color || '#6366f1' }} />
              <h1 className="text-2xl font-bold text-slate-900">{list.name}</h1>
              <Badge variant="secondary" className="gap-1">
                <Users className="h-3 w-3" />
                {leads.length} leads
              </Badge>
            </div>
            {list.description && (
              <p className="text-slate-500 mt-1 ml-7">{list.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={loadList} title="Refresh" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button 
            onClick={() => setEmailMergeOpen(true)} 
            disabled={leadsWithEmail.length === 0}
            data-testid="send-bulk-email-btn"
          >
            <Mail className="h-4 w-4 mr-2" /> 
            Send Bulk Email
            {leadsWithEmail.length > 0 && (
              <Badge className="ml-2 bg-white/20">{leadsWithEmail.length}</Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-slate-500">Total Leads</div>
            <div className="text-2xl font-bold text-slate-900">{leads.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-slate-500">With Email</div>
            <div className="text-2xl font-bold text-green-600">{leadsWithEmail.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-slate-500">New</div>
            <div className="text-2xl font-bold text-blue-600">
              {leads.filter(l => l.status === 'new').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-slate-500">Contacted</div>
            <div className="text-2xl font-bold text-yellow-600">
              {leads.filter(l => l.status === 'contacted').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leads Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Leads in this List</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search leads..."
                className="pl-9"
                data-testid="search-leads"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredLeads.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {searchQuery ? 'No leads match your search' : 'No leads in this list yet'}
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate('/admin/franchise/leads')}
              >
                <UserPlus className="h-4 w-4 mr-2" /> Go to Leads
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium text-slate-600">Name</th>
                    <th className="pb-3 font-medium text-slate-600">Email</th>
                    <th className="pb-3 font-medium text-slate-600">Company</th>
                    <th className="pb-3 font-medium text-slate-600">Location</th>
                    <th className="pb-3 font-medium text-slate-600">Status</th>
                    <th className="pb-3 font-medium text-slate-600 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(lead => (
                    <tr 
                      key={lead.id} 
                      className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/admin/franchise/leads/${lead.id}`)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="py-3">
                        <div className="font-medium text-slate-900">
                          {lead.first_name} {lead.last_name}
                        </div>
                      </td>
                      <td className="py-3">
                        <span className={`text-sm ${lead.email || lead.work_email ? 'text-slate-600' : 'text-slate-400 italic'}`}>
                          {lead.email || lead.work_email || 'No email'}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-slate-600">
                        {lead.current_employer || '-'}
                      </td>
                      <td className="py-3 text-sm text-slate-600">
                        {[lead.city, lead.state].filter(Boolean).join(', ') || '-'}
                      </td>
                      <td className="py-3">
                        {getStatusBadge(lead.status)}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/franchise/leads/${lead.id}`);
                            }}
                            title="View Lead"
                          >
                            <ExternalLink className="h-4 w-4 text-slate-400" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLeadToRemove(lead);
                              setRemoveDialogOpen(true);
                            }}
                            title="Remove from list"
                            data-testid={`remove-lead-${lead.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Merge Dialog */}
      <EmailMergeDialog
        open={emailMergeOpen}
        onOpenChange={(open) => {
          setEmailMergeOpen(open);
          // Clear action param when closing
          if (!open && searchParams.get('action') === 'email') {
            navigate(`/admin/franchise/lists/${id}`, { replace: true });
          }
        }}
        preselectedListId={id}
        templates={templates}
        onSuccess={() => {
          toast.success('Emails sent successfully!');
          loadList();
        }}
      />

      {/* Remove Lead Dialog */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Lead from List</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove &quot;{leadToRemove?.first_name} {leadToRemove?.last_name}&quot; from this list? 
              The lead will not be deleted, only removed from &quot;{list.name}&quot;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveLead} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
